import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import * as fromGenerated from '../../../_generated';
// Import the concrete reducer file, not the assistants barrel: this feature's
// extraSelectors dereference assistantsFeature at module-eval time, and going
// through the barrel trips the assistants↔teammates import cycle under jest's
// CJS interop (same precedent as chats.selectors.ts).
import { assistantsFeature } from '../../assistants/store/assistants.reducer';
import { TeammatesActions } from './teammates.actions';

export const teammatesFeatureKey = 'teammates';

export interface TeammatesState extends EntityState<fromGenerated.TeammateView> {
  loaded: boolean;
  error: string | null;
}

const entityAdapter: EntityAdapter<fromGenerated.TeammateView> =
  createEntityAdapter<fromGenerated.TeammateView>({
    selectId: (view) => view.id,
    sortComparer: (a, b) => a.name.localeCompare(b.name),
  });

export const initialTeammatesState: TeammatesState =
  entityAdapter.getInitialState({
    loaded: false,
    error: null,
  });

export const teammatesFeature = createFeature({
  name: teammatesFeatureKey,
  reducer: createReducer(
    initialTeammatesState,
    on(TeammatesActions.loadTeammatesSuccess, (state, { teammates }) =>
      entityAdapter.setAll(teammates, {
        ...state,
        loaded: true,
        error: null,
      }),
    ),
    on(TeammatesActions.loadTeammatesFailure, (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })),
  ),
  extraSelectors: ({ selectTeammatesState }) => {
    const entitySelectors = entityAdapter.getSelectors(selectTeammatesState);
    const selectAllWithAssistants = createSelector(
      entitySelectors.selectAll,
      assistantsFeature.selectAll,
      (teammates, assistants) =>
        teammates
          .map((teammate) => ({
            ...teammate,
            assistants: assistants.filter((assistant) =>
              teammate.assistantIds.includes(assistant.uuid),
            ),
          }))
          .filter((teammate) => teammate.assistants.length > 0),
    );

    return {
      ...entitySelectors,
      selectAllWithAssistants,
      selectTeammateBySlug: (slug: fromGenerated.TeammateView['slug']) =>
        createSelector(selectAllWithAssistants, (teammates) =>
          teammates.find((teammate) => teammate.slug === slug),
        ),
    };
  },
});
